
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Flex,
  useColorModeValue,
  useBreakpointValue,
  Spinner,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Avatar,
  Tag,
  Divider,
  Icon,
  Tooltip
} from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';
import {
  Calendar,
  momentLocalizer,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { FaUserAlt, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';

import { urlvideo } from './../services/urls';
import EventCard from './EventCard';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import EditEventModal from './EditEventModal';
import axiosInstance from '../services/axiosInstance';

moment.locale('ar-dz');

const localizer = momentLocalizer(moment);

const CalendarAdmin = ({ events, onDateClick, fetchEvents }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
 const [isLoading, setIsLoading] = useState(false); // Add this line
  const [error, setError] = useState(null); // Add this 
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const calendarBg = useColorModeValue("white", "gray.800");
  const eventBgColor = useColorModeValue('#3182ce', '#2b6cb0');
  const modalBg = useColorModeValue("white", "gray.700");
  const descriptionBg = useColorModeValue('gray.100', 'gray.600');
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "gray.200");

  const isMobile = useBreakpointValue({ base: true, md: false });
  const calendarHeight = useBreakpointValue({
    base: 'calc(100vh - 120px)',
    md: 'calc(100vh - 32px)'
  });

  const formattedEvents = useMemo(() =>
    events.map(event => ({
      ...event,
      start: moment(event.start).toDate(),
      end: moment(event.end).toDate(),
    })),
    [events]
  );

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    onOpen();
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setEditModalOpen(true);
  };

  const handleDelete = (event) => {
    setSelectedEvent(event);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axiosInstance.delete(`/events/${selectedEvent._id}`);
      setDeleteModalOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('خطأ في حذف الحدث:', error);
    }
  };

  const handleSaveEdit = async (updatedEvent) => {
    try {
      await axiosInstance.put(`/events/${updatedEvent._id}`, updatedEvent);
      fetchEvents();
    } catch (error) {
      console.error('خطأ في تحديث الحدث:', error);
    }
  };

  const handleDateClick = (start) => {
    const end = start; // Set end date to be the same as start date
    onDateClick({ start, end });
  };

  const eventPropGetter = () => ({
    style: {
      backgroundColor: eventBgColor,
      borderRadius: '6px',
      border: 'none',
      color: 'white',
      padding: '2px 5px',
      fontSize: '0.85rem',
      cursor: 'pointer'
    },
  });

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="100vh" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" thickness="4px" color="blue.500" />
          <Text>جاري تحميل أحداث التقويم...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" height="100vh" p={4} dir="rtl">
        <Alert
          status="error"
          borderRadius="md"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Text mt={4} fontSize="lg" fontWeight="bold">
            فشل تحميل التقويم
          </Text>
          <Text mt={2} color="gray.600">
            {error.message || 'الرجاء المحاولة مرة أخرى لاحقًا'}
          </Text>
          <Button mt={4} colorScheme="red" onClick={() => window.location.reload()}>
            إعادة المحاولة
          </Button>
        </Alert>
      </Flex>
    );
  }

  return (
    <Box width="100%" minHeight="100vh" p={4} bg={bgColor} dir="rtl">
      <Box
        bg={calendarBg}
        borderRadius="xl"
        boxShadow="xl"
        height={calendarHeight}
        overflow="hidden"
        position="relative"
      >
        <Calendar
          localizer={localizer}
          events={formattedEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%', padding: '0 10px' }}
          components={{
            event: (eventProps) => (
              <EventCard
                event={eventProps.event}
                onClick={() => handleEventClick(eventProps.event)}
                onEdit={() => handleEdit(eventProps.event)}
                onDelete={() => handleDelete(eventProps.event)}
              />
            ),
          }}
          popup
          views={['month', 'agenda']}
          onSelectSlot={({ start }) => handleDateClick(start)}
          selectable
          messages={{
            next: 'التالي',
            previous: 'السابق',
            today: 'اليوم',
            month: 'شهر',
            week: 'أسبوع',
            day: 'يوم',
            agenda: 'جدول الأعمال',
            date: 'التاريخ',
            time: 'الوقت',
            event: 'الحدث',
            allDay: 'طوال اليوم',
            noEventsInRange: 'لا توجد أحداث في هذا النطاق.',
            showMore: total => `+ ${total} المزيد`,
          }}
        />
      </Box>

      {/* Event Details Modal */}
      {selectedEvent && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size={isMobile ? "full" : "lg"}
          motionPreset="slideInBottom"
        >
          <ModalOverlay backdropFilter="blur(5px)" />
          <ModalContent bg={modalBg} dir="rtl">
            <ModalHeader borderBottomWidth="1px" pb={3}>
              <Flex align="center" flexDirection="row-reverse">
                <Avatar
                  src={urlvideo + selectedEvent?.imageUrl}
                  name={selectedEvent?.title}
                  size="md"
                  ml={3}
                />
                <Box textAlign="right">
                  <Text fontSize="xl" fontWeight="bold">{selectedEvent?.title}</Text>
                  <Text fontSize="sm" color="gray.500">{selectedEvent?.professor}</Text>
                </Box>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={4}>
              {selectedEvent && (
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4} justifyContent="flex-end">
                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" color="gray.500">وقت البدء</Text>
                      <Tag colorScheme="blue">
                        <HStack>
                          <FaCalendarAlt />
                          <Text>{moment(selectedEvent.start).format('MMMM Do, YYYY')}</Text>
                        </HStack>
                      </Tag>
                      <Tag colorScheme="blue" mt={1}>
                        <HStack>
                          <FaClock />
                          <Text>{moment(selectedEvent.start).format('h:mm A')}</Text>
                        </HStack>
                      </Tag>
                    </VStack>

                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" color="gray.500">وقت الانتهاء</Text>
                      <Tag colorScheme="red">
                        <HStack>
                          <FaCalendarAlt />
                          <Text>{moment(selectedEvent.end).format('MMMM Do, YYYY')}</Text>
                        </HStack>
                      </Tag>
                      <Tag colorScheme="red" mt={1}>
                        <HStack>
                          <FaClock />
                          <Text>{moment(selectedEvent.end).format('h:mm A')}</Text>
                        </HStack>
                      </Tag>
                    </VStack>
                  </HStack>

                  {selectedEvent.location && (
                    <Box textAlign="right">
                      <Text fontSize="sm" color="gray.500">الموقع</Text>
                      <Tag colorScheme="green">
                        <HStack>
                          <FaMapMarkerAlt />
                          <Text>{selectedEvent.location}</Text>
                        </HStack>
                      </Tag>
                    </Box>
                  )}

                  <Divider />

                  {selectedEvent.description ? (
                    <Box textAlign="right">
                      <HStack mb={2} justifyContent="flex-end">
                        <Icon as={FaInfoCircle} color="blue.500" />
                        <Text fontWeight="semibold">الوصف</Text>
                      </HStack>
                      <Text
                        whiteSpace="pre-line"
                        p={3}
                        bg={descriptionBg}
                        borderRadius="md"
                      >
                        {selectedEvent.description}
                      </Text>
                    </Box>
                  ) : (
                    <Text color="gray.500" fontStyle="italic" textAlign="right">لا يوجد وصف مقدم</Text>
                  )}
                </VStack>
              )}
            </ModalBody>
            <Flex justifyContent="flex-start" p={4} borderTopWidth="1px">
              <Button
                colorScheme="blue"
                onClick={onClose}
                size={isMobile ? "md" : "sm"}
              >
                إغلاق
              </Button>
            </Flex>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {selectedEvent && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteConfirm}
          event={selectedEvent}
        />
      )}

      {/* Edit Event Modal */}
      {selectedEvent && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          event={selectedEvent}
          onSave={handleSaveEdit}
        />
      )}
    </Box>
  );
};

export default CalendarAdmin;


